import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuBanknote,
  LuCreditCard,
  LuWallet,
  LuSplit,
  LuDelete,
  LuCheck,
  LuPrinter,
  LuReceipt,
} from "react-icons/lu";
import { TpvDialog } from "../../../components/TpvDialog";
import {
  initRequestPayments,
  updateRequestActivePayments,
} from "../../../reducers/userConfigSlice";
import { getOrderTotalWithTax } from "../../../utils/pricing";
import { printTicket } from "../../../api/orders";
import { OrderDetails } from "../../history/orderDetails";
const money = (value) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(
    value,
  );
export const CheckoutDialog = ({
  total,
  onEnsureOrder,
  onConfirm,
  onClose,
}) => {
  const dispatch = useDispatch();
  const methods =
      useSelector(
        (s) => s.aplicationConfig.corporationData.pointMethods.payments,
      ) || [],
    payments =
      useSelector(
        (s) => s.aplicationConfig.userConfig.request.activePayments,
      ) || [];
  const [mode, setMode] = useState("methods"),
    [method, setMethod] = useState(null),
    [actualTotal, setTotal] = useState(total),
    [digits, setDigits] = useState(""),
    [splitInputs, setSplitInputs] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [receipt, setReceipt] = useState(null),
    [view, setView] = useState(false);
  const pending = useRef(false);
  const active = methods.filter((m) => m.isActive === true),
    cash = active.find((m) => m.name?.toLocaleLowerCase("es") === "efectivo"),
    card = active.find((m) =>
      ["tarjeta", "tarjeta de credito", "tarjeta de debito"].includes(
        m.name
          ?.toLocaleLowerCase("es")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, ""),
      ),
    );
  const received =
    mode === "cash"
      ? Number(digits || 0) / 100
      : payments.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
  const missing = Math.max(0, actualTotal - received),
    change = Math.max(0, received - actualTotal);
  const choose = async (next, phase) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const order = await onEnsureOrder();
      const amount = getOrderTotalWithTax(order);
      if (!order?.orderId || !Number.isFinite(amount))
        throw Error("No se pudo confirmar el importe del pedido.");
      setTotal(amount);
      dispatch(initRequestPayments());
      setMethod(next);
      setDigits("");
      setSplitInputs({});
      if (phase === "card")
        dispatch(
          updateRequestActivePayments({
            paymentMethodId: next.paymentMethodId,
            value: Number(amount.toFixed(2)),
          }),
        );
      setMode(phase);
    } catch (e) {
      setError(e.message || "No se pudo preparar el cobro.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const amount = (next) => {
    setDigits(next);
    if (method)
      dispatch(
        updateRequestActivePayments({
          paymentMethodId: method.paymentMethodId,
          value: Number(next || 0) / 100,
        }),
      );
  };
  useEffect(() => {
    if (mode !== "cash" || busy) return;
    const handle = (event) => {
      if (event.target.closest?.("input,textarea")) return;
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        amount((digits + event.key).replace(/^0+/, "").slice(0, 9));
      } else if (event.key === "Backspace") {
        event.preventDefault();
        amount(digits.slice(0, -1));
      } else if (event.key.toLowerCase() === "c") {
        event.preventDefault();
        amount("");
      }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
    // amount updates the currently selected method and digit buffer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, busy, digits, method]);
  const setTender = (paymentMethodId, value) => {
    if (!/^\d{0,7}(?:[.,]\d{0,2})?$/.test(value)) return;
    setSplitInputs((previous) => ({ ...previous, [paymentMethodId]: value }));
    dispatch(
      updateRequestActivePayments({
        paymentMethodId,
        value: Number(value.replace(",", ".")) || 0,
      }),
    );
  };
  const confirm = async () => {
    if (
      pending.current ||
      missing > 0.000001 ||
      (actualTotal !== 0 && !payments.some((p) => Number(p.value) > 0))
    )
      return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await onConfirm({ expectedTotal: actualTotal });
      if (result?.order?.paymentStatus !== "paid")
        throw Error(
          "No se ha confirmado el cobro. Revisa el pedido antes de repetir.",
        );
      setReceipt(result);
    } catch (e) {
      setError(e.message || "No se pudo confirmar el cobro.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const print = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await printTicket({ orderId: receipt.order.orderId });
      if (response?.error !== false)
        throw Error(response?.message || "No se pudo imprimir el ticket.");
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  if (view)
    return (
      <OrderDetails
        orderDetailsModal={{ id: receipt.order.orderId }}
        setOrderDetailsModal={() => setView(false)}
      />
    );
  if (receipt)
    return (
      <TpvDialog
        title="Cobro completado"
        onClose={onClose}
        busy={busy}
        className="tpvReceiptDialog"
      >
        <div className="tpvReceiptBody">
          <span className="tpvReceiptCheck">
            <LuCheck aria-hidden="true" />
          </span>
          <h2>
            PEDIDO #{receipt.order.displayNumber || receipt.order.number}{" "}
            COBRADO
          </h2>
          <strong>{money(receipt.total)}</strong>
          <p>
            {receipt.method} · Cambio {money(receipt.change)}
          </p>
          {receipt.warning && <p role="alert">{receipt.warning}</p>}
          {error && <p role="alert">{error}</p>}
          <button className="tpvPrimary" onClick={onClose} disabled={busy}>
            NUEVO PEDIDO
          </button>
          <div>
            <button onClick={print} disabled={busy}>
              <LuPrinter aria-hidden="true" /> Imprimir ticket
            </button>
            <button onClick={() => setView(true)} disabled={busy}>
              <LuReceipt aria-hidden="true" /> Ver pedido
            </button>
          </div>
        </div>
      </TpvDialog>
    );
  return (
    <TpvDialog
      title={
        mode === "methods"
          ? "Cobrar"
          : mode === "cash"
            ? "Cobrar · Efectivo"
            : mode === "card"
              ? "Cobrar · Tarjeta"
              : mode === "other"
                ? "Otro método"
                : "Dividir pago"
      }
      onClose={onClose}
      busy={busy}
      className="tpvCheckoutDialog"
    >
      <div className="tpvDialogBody">
        <div className="tpvCheckoutTotal">
          <small>TOTAL A COBRAR</small>
          <strong>{money(actualTotal)}</strong>
        </div>
        {error && (
          <p className="tpvDialogError" role="alert">
            {error}
          </p>
        )}
        {mode === "methods" ? (
          <>
            <h3>¿Cómo paga el cliente?</h3>
            <div className="tpvPaymentChoices">
              <button
                disabled={busy || !cash}
                onClick={() => choose(cash, "cash")}
              >
                <LuBanknote aria-hidden="true" />
                <strong>EFECTIVO</strong>
              </button>
              <button
                disabled={busy || !card}
                onClick={() => choose(card, "card")}
              >
                <LuCreditCard aria-hidden="true" />
                <strong>TARJETA</strong>
              </button>
              <button
                disabled={busy || !active.length}
                onClick={() => choose(null, "other")}
              >
                <LuWallet aria-hidden="true" /> Otro método
              </button>
              <button
                disabled={busy || active.length < 2}
                onClick={() => choose(null, "split")}
              >
                <LuSplit aria-hidden="true" /> Dividir pago
              </button>
            </div>
            {busy && <p role="status">Preparando el pedido…</p>}
          </>
        ) : mode === "cash" ? (
          <div className="tpvCashBody">
            <div>
              <small>IMPORTE RECIBIDO</small>
              <output aria-label="Importe recibido" className="tpvCashReceived">
                {money(received)}
              </output>
              <div className="tpvCashShortcuts">
                <button
                  disabled={busy}
                  onClick={() => amount(String(Math.round(actualTotal * 100)))}
                >
                  Exacto
                </button>
                {[
                  ...new Set([
                    Math.ceil(actualTotal),
                    Math.ceil(actualTotal / 5) * 5,
                    Math.ceil(actualTotal / 5) * 5 + 5,
                    Math.ceil(actualTotal / 5) * 5 + 25,
                  ]),
                ].map((value) => (
                  <button
                    key={value}
                    disabled={busy}
                    onClick={() => amount(String(value * 100))}
                  >
                    {money(value)}
                  </button>
                ))}
              </div>
              <div className="tpvCashSummary">
                <div>
                  RECIBIDO<strong>{money(received)}</strong>
                </div>
                <div>
                  {missing > 0 ? "FALTAN" : "CAMBIO"}
                  <strong>{money(missing > 0 ? missing : change)}</strong>
                </div>
              </div>
            </div>
            <div className="tpvCashKeypad">
              {[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "C",
                "0",
                "delete",
              ].map((key) => (
                <button
                  type="button"
                  key={key}
                  disabled={busy}
                  aria-label={
                    key === "delete"
                      ? "Borrar última cifra"
                      : key === "C"
                        ? "Limpiar importe"
                        : key
                  }
                  onClick={() =>
                    amount(
                      key === "C"
                        ? ""
                        : key === "delete"
                          ? digits.slice(0, -1)
                          : (digits + key).replace(/^0+/, "").slice(0, 9),
                    )
                  }
                >
                  {key === "delete" ? <LuDelete aria-hidden="true" /> : key}
                </button>
              ))}
            </div>
          </div>
        ) : mode === "card" ? (
          <div className="tpvCardConfirmation">
            <LuCreditCard aria-hidden="true" />
            <h3>Tarjeta · {money(actualTotal)}</h3>
            <p>Confirma después de realizar el cobro en el terminal.</p>
          </div>
        ) : (
          <>
            <div className="tpvTenderRows">
              {active.map((m) => (
                <div className="tpvTenderRow" key={m.paymentMethodId}>
                  <label>
                    <strong>{m.name}</strong>
                    <span>
                      <input
                        aria-label={`Importe ${m.name}`}
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        disabled={busy}
                        value={splitInputs[m.paymentMethodId] || ""}
                        onChange={(event) =>
                          setTender(m.paymentMethodId, event.target.value)
                        }
                      />{" "}
                      €
                    </span>
                  </label>
                  <button
                    disabled={busy}
                    onClick={() => {
                      const others = payments
                        .filter((p) => p.paymentMethodId !== m.paymentMethodId)
                        .reduce((sum, p) => sum + (Number(p.value) || 0), 0);
                      setTender(
                        m.paymentMethodId,
                        Math.max(0, actualTotal - others).toFixed(2),
                      );
                    }}
                  >
                    Asignar restante
                  </button>
                </div>
              ))}
            </div>
            <div className="tpvCashSummary">
              <div>
                RECIBIDO<strong>{money(received)}</strong>
              </div>
              <div>
                {missing > 0 ? "FALTAN" : "CAMBIO"}
                <strong>{money(missing > 0 ? missing : change)}</strong>
              </div>
            </div>
          </>
        )}
      </div>
      {mode !== "methods" && (
        <footer className="tpvCheckoutFooter">
          <button
            disabled={busy}
            onClick={() => {
              dispatch(initRequestPayments());
              setMode("methods");
              setError("");
            }}
          >
            ‹ Método
          </button>
          <button
            className="tpvPrimary"
            disabled={
              busy ||
              missing > 0.000001 ||
              (actualTotal !== 0 && !payments.some((p) => Number(p.value) > 0))
            }
            onClick={confirm}
          >
            {busy ? "CONFIRMANDO…" : "CONFIRMAR COBRO"}
          </button>
        </footer>
      )}
    </TpvDialog>
  );
};
